import { parse, stringify } from 'querystring';
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate as useRouterNavigate } from 'react-router-dom';
import { useParseQueryParam } from '../pages/account-selection/hook/useParseQueryParam';
import { Feature, getIntervalPathParam, getQuotaPathParam, getRecurlySubUuIdPathParam } from '../models';
import {
  CHECKOUT_ADDED_PRODUCTS_QUERY_PARAM,
  PLAN_BILLING_QUERY_PARAM,
  PLAN_FEATURE_QUERY_PARAM,
  PLAN_QUANTITY_QUERY_PARAM,
  PLAN_SUBSCRIPTION_ID_QUERY_PARAM,
  PRODUCT_QUERY_PARAMS,
  RETIRED_SELF_SERVE_FEATURES,
  SUPPORTED_FEATURES,
} from '../utils/Constants';

type QueryType = string | number | boolean | undefined | string[];
export type QueryDictionary<T = unknown, K extends keyof T = keyof T> = Record<K, QueryType>;

interface MergeQuery<T = unknown> {
  type: 'MERGE_QUERY';
  dictionary: QueryDictionary<T>;
}

interface ReplaceQuery<T = unknown> {
  type: 'REPLACE_QUERY',
  dictionary: QueryDictionary<T>;
}

function isReplaceQuery(arg: any): arg is ReplaceQuery {
  return arg.type === 'REPLACE_QUERY';
}

type QueryConfig<T> = MergeQuery<T> | ReplaceQuery<T>;
type QueryStringDictionary = Record<string, QueryType>;

const PLAN_QUERY_ALLOWED_PATHS = [
  '/plan',
  '/plan/purchase',
  '/plan/order-confirmation',
  '/plan/confirm-payment',
  '/select-account',
];

const isPlanQueryAllowedPath = (pathname: string) => PLAN_QUERY_ALLOWED_PATHS.some(
  path => pathname === path || pathname.endsWith(path) || pathname.includes(`${path}?`),
);

const deletePlanSelectionParams = (dictionary: QueryStringDictionary) => {
  [
    PLAN_FEATURE_QUERY_PARAM,
    PLAN_QUANTITY_QUERY_PARAM,
    PLAN_BILLING_QUERY_PARAM,
    PLAN_SUBSCRIPTION_ID_QUERY_PARAM,
    CHECKOUT_ADDED_PRODUCTS_QUERY_PARAM,
    ...PRODUCT_QUERY_PARAMS,
  ].forEach(param => {
    delete dictionary[param];
  });

  // Retired SKUs are swept too: their params are no longer readable anywhere, so leaving them in
  // the URL would only make them outlive the page that once used them (R6 / Decision 16).
  [...SUPPORTED_FEATURES, ...RETIRED_SELF_SERVE_FEATURES].forEach(feature => {
    delete dictionary[getQuotaPathParam(feature)];
    delete dictionary[getIntervalPathParam(feature)];
    delete dictionary[getRecurlySubUuIdPathParam(feature)];
  });
};

export const sanitizeQueryForPath = (
  dictionary: QueryStringDictionary,
  pathname: string,
): QueryStringDictionary => {
  const nextDictionary = { ...dictionary };

  if (!isPlanQueryAllowedPath(pathname)) {
    deletePlanSelectionParams(nextDictionary);
    return nextDictionary;
  }

  const planFeature = Feature.fromString(
    nextDictionary[PLAN_FEATURE_QUERY_PARAM]?.toString().trim().toUpperCase() ?? '',
  );
  if (planFeature
    && (nextDictionary[getQuotaPathParam(planFeature)] !== undefined
      || nextDictionary[getIntervalPathParam(planFeature)] !== undefined)) {
    [
      PLAN_FEATURE_QUERY_PARAM,
      PLAN_QUANTITY_QUERY_PARAM,
      PLAN_BILLING_QUERY_PARAM,
    ].forEach(param => {
      delete nextDictionary[param];
    });
  }

  return nextDictionary;
};

export const useNavigate = <T = any>() => {
  const routerNavigate = useRouterNavigate();
  const location = useLocation();
  const queryParam = useParseQueryParam();
  const convertSearchString = useCallback((
    query: QueryConfig<T>,
    pathname: string | undefined,
  ) => {
    const targetPath = pathname ?? location.pathname;
    return isReplaceQuery(query)
      ? stringify(sanitizeQueryForPath({
        ...query.dictionary,
        ...queryParam,
      }, targetPath))
      : stringify(sanitizeQueryForPath({
        ...parse(location.search.replace('?', '')),
        ...query.dictionary,
        ...queryParam,
      }, targetPath));
  }, [location.pathname, location.search, queryParam]);
  const navigate = useCallback((pathname: string | undefined, query: QueryConfig<T>) => {
    routerNavigate(
      {
        pathname: pathname ? `../../../../${pathname}` : `../../../../${location.pathname}`,
        search: convertSearchString(query, pathname),
      },
    );
  }, [routerNavigate, location.pathname, convertSearchString]);
  const replace = useCallback((pathname: string | undefined, query: QueryConfig<T>) => {
    routerNavigate(
      {
        pathname: pathname ?? location.pathname,
        search: convertSearchString(query, pathname),
      },
      {
        replace: true,
      },
    );
  }, [routerNavigate, location.pathname, convertSearchString]);
  const mergeQuery = useCallback((query: QueryDictionary<T>): MergeQuery<T> => ({
    type: 'MERGE_QUERY',
    dictionary: query,
  }), []);
  const replaceQuery = useCallback((query: QueryDictionary<T>): ReplaceQuery<T> => ({
    type: 'REPLACE_QUERY',
    dictionary: {
      ...query,
      ...queryParam,
    },
  }), []);

  const redirect = ((uri: string | undefined) => {
    if (typeof uri === 'string') {
      window.location.href = uri;
    }
  });

  return useMemo(() => ({
    navigate,
    replace,
    mergeQuery,
    replaceQuery,
    redirect,
  }), [navigate, replace, mergeQuery, replaceQuery]);
};
